// Shared building blocks for the standardised list-page header
// (total-count label on the left, centered search bar). Mirrors the
// shape introduced in InventoryManagement and is consumed by the
// Closed Hours admin page and any future list-page header.

import { useEffect, useRef, useState, type ReactNode } from 'react';

// Shared components
import { SearchIcon } from '../icons';
import { BORDER_RADIUS_CLASSES } from '../DataTable/constants';

// Utilities
import { SEARCH_BAR_WIDTH } from '../../constants';
import { MIN_SEARCH_LENGTH } from '@/react/utils/searchQuery';

const CENTERED_ROW_TEMPLATE = `minmax(max-content, 1fr) minmax(0, ${SEARCH_BAR_WIDTH}px) minmax(0, 1fr)`;

interface PageHeaderCenteredRowProps {
  start: ReactNode;
  children: ReactNode;
}

export function PageHeaderCenteredRow({ start, children }: PageHeaderCenteredRowProps) {
  return (
    <div className="grid w-full items-center gap-4" style={{ gridTemplateColumns: CENTERED_ROW_TEMPLATE }}>
      {start}
      {children}
    </div>
  );
}

interface PageHeaderTotalLabelProps {
  count: number;
  label: string;
}

export function PageHeaderTotalLabel({ count, label }: PageHeaderTotalLabelProps) {
  return (
    <span className="font-inter text-sm font-normal leading-[1.5] text-grey">
      {label.replace('%{count}', count.toLocaleString())}
    </span>
  );
}

interface PageHeaderSearchBarProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  ariaLabel: string;
  // Optional keyboard shortcut: when set, pressing <modifier>+<key>
  // anywhere on the page focuses the input. A clickable kbd hint
  // rendered on the right edge of the search bar does the same.
  shortcutKey?: string;
  shortcutModifier?: 'alt' | 'meta';
  shortcutAriaLabel?: string;
  // Show a "keep typing" hint until the term is long enough to search.
  // Defaults to the shared MIN_SEARCH_LENGTH; pass a different value (or the
  // same) — all list pages gate at MIN_SEARCH_LENGTH via searchQueryParam.
  minSearchLength?: number;
}

export function PageHeaderSearchBar({
  value,
  onChange,
  placeholder,
  ariaLabel,
  shortcutKey,
  shortcutModifier = 'alt',
  shortcutAriaLabel,
  minSearchLength = MIN_SEARCH_LENGTH,
}: PageHeaderSearchBarProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!shortcutKey) return;
    // Match against `event.code` (physical key) instead of `event.key`
    // (character produced) — on macOS, Option+F yields key="ƒ" (florin
    // sign), so checking key === "f" would never match. `code` stays
    // "KeyF" regardless of modifiers and is consistent across layouts
    // for letter keys.
    const expectedCode = `Key${shortcutKey.toUpperCase()}`;
    const handler = (e: KeyboardEvent) => {
      if (e.code !== expectedCode) return;
      // Strict modifier match — only the configured modifier may be
      // held. Stops Cmd+Option+F (Firefox "find again"), Shift+Option+F
      // (formatters), Ctrl+Alt+F (some Linux DEs), etc. from
      // hijacking focus.
      if (shortcutModifier === 'meta') {
        if (!e.metaKey || e.altKey || e.ctrlKey || e.shiftKey) return;
      } else {
        if (!e.altKey || e.metaKey || e.ctrlKey || e.shiftKey) return;
      }
      // Bail out when focus is already in an editable element — the
      // user is mid-typing in another input/textarea/contentEditable;
      // stealing focus to the search bar would interrupt them.
      const active = document.activeElement as HTMLElement | null;
      const inEditable =
        active != null &&
        active !== inputRef.current &&
        (active.tagName === 'INPUT' ||
          active.tagName === 'TEXTAREA' ||
          active.isContentEditable);
      if (inEditable) return;
      e.preventDefault();
      inputRef.current?.focus();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [shortcutKey, shortcutModifier]);

  const showShortcut = Boolean(shortcutKey);
  const trimmedLength = value.trim().length;
  // Only guide while the user is actively in the field — a 1-2 char term left
  // behind on blur shouldn't keep the hint (and the box extension) showing.
  const showHint = focused && trimmedLength > 0 && trimmedLength < minSearchLength;

  return (
    // The box is absolutely positioned over an invisible spacer that holds the
    // collapsed footprint. `Page.Header` is fixed-height and centers its
    // children, so a box that grew in flow would re-center and shift the input
    // up. Anchoring the box to the spacer's top keeps the input pinned; the
    // box (a single element, so one focus ring wraps input + hint) just
    // extends downward when the hint shows.
    <div className="relative w-full" style={{ maxWidth: SEARCH_BAR_WIDTH }}>
      {/* Collapsed footprint: same row height + vertical padding as the box,
          so the header reserves and centers the resting size. */}
      <div aria-hidden="true" className="invisible flex items-center py-4">
        <span className="size-5" />
        <span className="font-inter text-sm leading-[1.5]">&nbsp;</span>
      </div>
      <div
        className={`absolute inset-x-0 top-0 z-20 bg-dinnerplate-white-light flex flex-col pl-6 ${showShortcut ? 'pr-4' : ''} py-4 focus-within:ring-1 focus-within:ring-off-black/30 ${BORDER_RADIUS_CLASSES}`}
      >
        <div className="flex gap-4 items-center w-full">
          <SearchIcon className="size-5 text-grey shrink-0" aria-hidden="true" />
          <input
            ref={inputRef}
            type="search"
            autoComplete="off"
            spellCheck={false}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder={placeholder}
            aria-label={ariaLabel}
            className="bg-transparent p-0 appearance-none font-inter text-sm font-normal leading-[1.5] text-off-black placeholder:text-grey w-full outline-none border-none shadow-none ring-0 focus:outline-none focus:ring-0 focus:shadow-none [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none"
          />
          {showShortcut && shortcutKey && (
            <button
              type="button"
              aria-label={shortcutAriaLabel ?? `Focus ${ariaLabel}`}
              // preventDefault on mousedown stops the browser from
              // focusing this button on click, which would otherwise
              // race with our onClick handler and leave focus on the
              // button instead of the input.
              onMouseDown={(e) => {
                e.preventDefault();
                inputRef.current?.focus();
              }}
              tabIndex={-1}
              className="flex items-center gap-1 shrink-0"
            >
              <kbd className="font-inter text-xs leading-none text-grey bg-off-white border border-dinnerplate-white rounded-xs px-1.5 py-1">
                {shortcutModifier === 'meta' ? '⌘' : '⌥'}
              </kbd>
              <kbd className="font-inter text-xs leading-none text-grey bg-off-white border border-dinnerplate-white rounded-xs px-1.5 py-1">
                {shortcutKey.toUpperCase()}
              </kbd>
            </button>
          )}
        </div>
        {showHint && (
          <span
            className="font-inter text-xs leading-none text-grey mt-1.5"
            aria-live="polite"
          >
            Type {minSearchLength} characters to start search
          </span>
        )}
      </div>
    </div>
  );
}
